import { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddLinkIcon from "@mui/icons-material/AddLink";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { confirmAlert } from "react-confirm-alert";
import { findAll as findAllSeries } from "../../Serie/api-serie";
import { dateFormat } from "../../../util/DateUtil";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { errorToast, success } from "../../../util/ToastUtil";
import Loading from "../../UI/Loading";
import { diaDaSemana, porData } from "./diaCalendarioUtil";

/** Diálogo com os dias ainda não vinculados à série, para marcar vários de uma vez. */
const VincularDialog = ({ open, onClose, config, serie, onSaved }) => {
  const { apiSerie, campoSerie } = config;
  const [disponiveis, setDisponiveis] = useState([]);
  const [selecionados, setSelecionados] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setIsLoading(true);
    setSelecionados(new Set());
    apiSerie
      .findByUnregistered(serie.codSerie)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else if (Array.isArray(data)) {
          setDisponiveis([...data].sort(porData((d) => d.data)));
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, apiSerie, serie.codSerie]);

  const toggle = (codigo) => {
    setSelecionados((prevState) => {
      const next = new Set(prevState);
      if (next.has(codigo)) next.delete(codigo);
      else next.add(codigo);
      return next;
    });
  };

  const todosMarcados = disponiveis.length > 0 && selecionados.size === disponiveis.length;

  const handleSave = () => {
    const dias = disponiveis.filter((d) => selecionados.has(d.codigo));
    setIsSaving(true);
    Promise.all(dias.map((dia) => apiSerie.save({ [campoSerie]: dia, serie }).then((data) => ({ dia, data }))))
      .then((results) => {
        const salvos = results.filter(({ data }) => data && !data.error).map(({ data }) => data);
        const falhas = results.length - salvos.length;
        if (salvos.length > 0) success(`${salvos.length} dia(s) vinculado(s) à série.`);
        if (falhas > 0) errorToast(`Não foi possível vincular ${falhas} dia(s).`);
        onSaved(salvos);
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Vincular {config.plural}
        <Typography variant="body2" color="text.secondary">
          Série {serie.descricao}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box py={4}>
            <Loading size={28} description="Carregando dias..." />
          </Box>
        ) : disponiveis.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
            Todos os {config.plural.toLowerCase()} já estão vinculados a esta série.
          </Typography>
        ) : (
          <>
            <Button
              size="small"
              onClick={() => setSelecionados(todosMarcados ? new Set() : new Set(disponiveis.map((d) => d.codigo)))}
              sx={{ mb: 1 }}
            >
              {todosMarcados ? "Desmarcar todos" : "Marcar todos"}
            </Button>
            <List dense sx={{ border: 1, borderColor: "divider", borderRadius: 2, maxHeight: 380, overflowY: "auto" }}>
              {disponiveis.map((dia) => (
                <ListItemButton key={dia.codigo} divider onClick={() => toggle(dia.codigo)}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox edge="start" size="small" checked={selecionados.has(dia.codigo)} tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${dateFormat(dia.data)} · ${diaDaSemana(dia.data)}`}
                    secondary={dia.descricao || undefined}
                  />
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" disabled={selecionados.size === 0 || isSaving} onClick={handleSave}>
          Vincular {selecionados.size > 0 ? selecionados.size : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Vínculo dos dias (Letivos Extras ou Não Letivos) com cada série. */
const DiaCalendarioSerie = ({ config }) => {
  const { apiSerie, campoSerie } = config;
  const [series, setSeries] = useState([]);
  const [serie, setSerie] = useState(null);
  const [vinculos, setVinculos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openVincular, setOpenVincular] = useState(false);
  const codSerie = serie?.codSerie;

  useEffect(() => {
    findAllSeries().then((data) => {
      if (!data) return;
      if (data.error) {
        errorMessage(data.message);
      } else if (Array.isArray(data)) {
        setSeries(data);
      }
    });
  }, []);

  useEffect(() => {
    setVinculos([]);
    if (!codSerie) return undefined;
    let active = true;
    setIsLoading(true);
    apiSerie
      .findByAnoLetivoAndSerieCodSerie(codSerie)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else if (Array.isArray(data)) {
          setVinculos([...data].sort(porData((v) => v[campoSerie]?.data)));
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [apiSerie, campoSerie, codSerie]);

  const handleRemove = (vinculo) => {
    confirmAlert({
      title: "Alerta",
      message: `Deseja desvincular o dia ${dateFormat(vinculo[campoSerie]?.data)} da série?`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            apiSerie.deleteById(vinculo.codigo).then((data) => {
              if (data?.error) {
                errorMessage(data.message);
                return;
              }
              setVinculos((prevState) => prevState.filter((v) => v.codigo !== vinculo.codigo));
              success("Dia desvinculado da série.");
            });
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  const handleSaved = (novos) => {
    setOpenVincular(false);
    setVinculos((prevState) => [...prevState, ...novos].sort(porData((v) => v[campoSerie]?.data)));
  };

  const renderVinculos = () => {
    if (!serie) {
      return (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={6}>
          Selecione uma série para ver os {config.plural.toLowerCase()} vinculados.
        </Typography>
      );
    }
    if (isLoading) {
      return (
        <Box py={6}>
          <Loading description="Carregando dias da série..." />
        </Box>
      );
    }
    if (vinculos.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={6}>
          Nenhum {config.singular.toLowerCase()} vinculado a esta série.
        </Typography>
      );
    }
    return (
      <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
        <Table size="small" aria-label={`${config.plural} da série`}>
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
              <TableCell sx={{ width: 180 }}>Data</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell align="right" sx={{ width: 64 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {vinculos.map((vinculo) => {
              const dia = vinculo[campoSerie];
              return (
                <TableRow key={vinculo.codigo} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {dateFormat(dia?.data)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>
                      {diaDaSemana(dia?.data)}
                    </Typography>
                  </TableCell>
                  <TableCell>{dia?.descricao || "--"}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Desvincular da série">
                      <IconButton size="small" onClick={() => handleRemove(vinculo)}>
                        <LinkOffIcon fontSize="small" color="error" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      {serie && (
        <VincularDialog
          open={openVincular}
          onClose={() => setOpenVincular(false)}
          config={config}
          serie={serie}
          onSaved={handleSaved}
        />
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mb={2} alignItems={{ sm: "center" }}>
        <Autocomplete
          options={series}
          value={serie}
          onChange={(event, value) => setSerie(value)}
          getOptionLabel={(option) => option.descricao ?? ""}
          isOptionEqualToValue={(option, value) => option.codSerie === value.codSerie}
          noOptionsText="Nenhuma série encontrada"
          renderInput={(params) => <TextField {...params} size="small" label="Série" />}
          sx={{ minWidth: 280, flexGrow: { sm: 0 } }}
        />
        <Box flexGrow={1} />
        <Button
          variant="contained"
          startIcon={<AddLinkIcon />}
          disabled={!serie}
          onClick={() => setOpenVincular(true)}
        >
          Vincular Dias
        </Button>
      </Stack>

      {renderVinculos()}
    </Box>
  );
};

export default DiaCalendarioSerie;
